/** The admin console's reads and writes (0.8.0) — always through the app's own API. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AboutResponse,
  AuditResponse,
  DiagnosticBundle,
  FixCandidate,
  MissingHomeVisitRowsCount,
  MissingHomeVisitRowsResult,
  MissingLinksCount,
  MissingLinksStep,
  FixResult,
  FixTool,
  MeResponse,
  ReviewResponse,
  SettingsResponse,
  UsersResponse,
  UserExtras,
} from '@shared/admin';
import type { FinishQuestions } from '@shared/finish';
import type { FeedbackListResponse } from '@shared/feedback';
import { apiFetch } from '@/lib/api';

export const send = <T,>(path: string, method: 'POST' | 'PUT', body: unknown) => apiFetch<T>(path, { method, body: JSON.stringify(body) });

export function useMeExtras(enabled = true) {
  return useQuery({ queryKey: ['me-extras'], queryFn: () => apiFetch<MeResponse>('/api/me'), enabled, staleTime: 10 * 60 * 1000, retry: false });
}

export function useUsers() {
  return useQuery({ queryKey: ['admin-users'], queryFn: () => apiFetch<UsersResponse>('/api/manage/users') });
}

export function useSaveExtras() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { email: string; extras: Partial<UserExtras> }) => send<{ user: unknown }>('/api/manage/users/extras', 'POST', a),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useSettingsRows() {
  return useQuery({ queryKey: ['admin-settings'], queryFn: () => apiFetch<SettingsResponse>('/api/manage/settings') });
}

export function useFinishQuestions() {
  return useQuery({ queryKey: ['admin-finish-questions'], queryFn: () => apiFetch<FinishQuestions>('/api/manage/finish-questions') });
}

export interface PlaceRow {
  index: number;
  category: string;
  name: string;
  address: string;
  phone: string;
  notes: string;
  walking_link: string;
  apple_link: string;
  retired?: boolean;
}

export function usePlaces() {
  return useQuery({ queryKey: ['admin-places'], queryFn: () => apiFetch<{ places: PlaceRow[] }>('/api/manage/places') });
}

export function useReviewQueue() {
  return useQuery({ queryKey: ['admin-review'], queryFn: () => apiFetch<ReviewResponse>('/api/manage/review') });
}

export function useAllFeedback() {
  return useQuery({ queryKey: ['admin-feedback'], queryFn: () => apiFetch<FeedbackListResponse>('/api/manage/feedback') });
}

export function useAudit(params: Record<string, string>) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  return useQuery({ queryKey: ['admin-audit', qs], queryFn: () => apiFetch<AuditResponse>(`/api/manage/audit${qs ? `?${qs}` : ''}`) });
}

export function fixLookup(tool: FixTool, q: string) {
  return apiFetch<{ candidates: FixCandidate[] }>(`/api/manage/fix/lookup?tool=${tool}&q=${encodeURIComponent(q)}`);
}

export function runFix(body: { tool: FixTool; record: string; reason: string; newDayKey?: string; retryMode?: 'resubmit' | 'resolve'; regenMode?: 'pattern' | 'lastMonth' }) {
  return send<FixResult>('/api/manage/fix', 'POST', body);
}

export function countMissingLinks() {
  return apiFetch<MissingLinksCount>('/api/manage/fix/links');
}

export function writeMissingLinksStep(offset: number) {
  return send<MissingLinksStep>('/api/manage/fix/links', 'POST', { offset });
}

export function countMissingHomeVisitRows() {
  return apiFetch<MissingHomeVisitRowsCount>('/api/manage/fix/home-visits');
}

export function writeMissingHomeVisitRows() {
  return send<MissingHomeVisitRowsResult>('/api/manage/fix/home-visits', 'POST', {});
}

export function fetchDiagnostic(check?: string | null) {
  return apiFetch<DiagnosticBundle & { markdown: string }>(`/api/manage/diagnostics${check ? `?check=${encodeURIComponent(check)}` : ''}`);
}

export function useAbout() {
  return useQuery({ queryKey: ['admin-about'], queryFn: () => apiFetch<AboutResponse>('/api/manage/about'), staleTime: 60 * 60 * 1000 });
}
